import React, { useState, useEffect } from 'react'
import SectionWelcome from '../../components/SectionWelcome.jsx'
import WalkingChallenge from '../Activities/WalkingChallenge.jsx'
import WalkMission from '../Activities/WalkMission.jsx'
import AdventureVault from '../Activities/AdventureVault.jsx'
import ColourWalk from './ColourWalk.jsx'
import JoyBingo from './JoyBingo.jsx'
import HobbySpinner from './HobbySpinner.jsx'
import GuidedWalk from './GuidedWalk.jsx'
import Card from '../../components/Card.jsx'
import { todayISO, dayIndex } from '../../utils/dates.js'
import * as api from '../../services/api.js'
import { COMPANIONS, COMPANION_ORDER, STEP_MILESTONES } from '../Activities/companionData.js'
import { DAILY_MISSIONS } from '../Activities/WalkMission.jsx'
import { COLOUR_WALK_PALETTE } from '../../utils/colours.js'

// ── Constants ────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'today',     label: 'Today' },
  { id: 'active',    label: 'Active' },
  { id: 'explore',   label: 'Explore' },
  { id: 'vault',     label: 'Vault' },
  { id: 'completed', label: 'Completed' },
]

const CATALOGUE = [
  {
    category: 'Movement',
    items: [
      { id: 'walking',    name: 'Walking Adventure', desc: 'Track your steps with a legendary companion', emoji: '🚶' },
      { id: 'colour-walk',name: 'Colour Walk',        desc: 'Spot colours from nature in the world around you', emoji: '🎨' },
      { id: 'guided-walk',name: 'Guided Walk',        desc: 'Guided observation walk with missions', emoji: '🌿', placeholder: true },
    ],
  },
  {
    category: 'Creative & Mindful',
    items: [
      { id: 'bingo',        name: 'Joy Bingo',      desc: 'Tick off small joys on your bingo card', emoji: '🎯' },
      { id: 'hobby-spinner',name: 'Hobby Spinner',  desc: 'Discover something new to try', emoji: '🎡' },
    ],
  },
  {
    category: 'Daily Discovery',
    items: [
      { id: 'walk-mission', name: 'Walk Missions', desc: 'Daily observation missions for your walks', emoji: '🎖️' },
    ],
  },
]

// ── Helpers ──────────────────────────────────────────────────────────────────

function scrollToSection(id) {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function calcMissionStreak(completions) {
  if (!completions?.length) return 0
  const unique = [...new Set(completions)].sort().reverse()
  const todayStr = new Date().toISOString().slice(0, 10)
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
  if (unique[0] !== todayStr && unique[0] !== yesterday) return 0
  let streak = 1
  for (let i = 1; i < unique.length; i++) {
    const diff = Math.round((new Date(unique[i - 1]) - new Date(unique[i])) / 86400000)
    if (diff === 1) streak++
    else break
  }
  return streak
}

function isBingoFull(done) { return done && done.length >= 25 }

// ── Section nav ───────────────────────────────────────────────────────────────

function ChallengesSectionNav({ activeSection }) {
  return (
    <div className="dashboard-section-nav">
      {SECTIONS.map(s => (
        <button
          key={s.id}
          className={`dashboard-nav-btn${activeSection === s.id ? ' active' : ''}`}
          onClick={() => scrollToSection(s.id)}
        >
          {s.label}
        </button>
      ))}
    </div>
  )
}

// ── Today section ─────────────────────────────────────────────────────────────

function TodaySection({ walkData, missionData, onLogProgress }) {
  const today = todayISO()
  const companion = COMPANIONS[walkData?.companion ?? 'carl'] ?? COMPANIONS.carl
  const compLog = walkData?.logs?.[companion.id] ?? []
  const todaySteps = (compLog.find(e => e.date === today) ?? { steps: 0 }).steps
  const goal = walkData?.goal || 5000
  const goalPct = Math.min((todaySteps / goal) * 100, 100)

  const totalSteps = Object.values(walkData?.logs || {}).reduce(
    (sum, log) => sum + (Array.isArray(log) ? log.reduce((s, e) => s + (e.steps || 0), 0) : 0), 0
  )
  const hasWalkProgress = totalSteps > 0

  const missionIdx = dayIndex() % DAILY_MISSIONS.length
  const todayMission = DAILY_MISSIONS[missionIdx]
  const missionDone = missionData?.completions?.includes(today) ?? false

  if (!hasWalkProgress) {
    return (
      <Card label="Today's Challenges" title="Nothing active yet 🌿">
        <p style={{ fontSize: '0.88rem', color: 'var(--stone)', fontStyle: 'italic', marginBottom: '1rem' }}>
          No active challenge today — why not explore one?
        </p>
        <div style={{ background: 'var(--surf)', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
          <p style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--stone)', marginBottom: '0.4rem' }}>
            {todayMission.emoji} Today's walk mission
          </p>
          <p style={{ fontSize: '0.92rem', lineHeight: 1.6, color: 'var(--ink)', margin: 0 }}>
            {todayMission.text}
          </p>
        </div>
        <button className="ghost-btn" style={{ fontSize: '0.8rem' }} onClick={() => scrollToSection('explore')}>
          Explore challenges →
        </button>
      </Card>
    )
  }

  return (
    <>
      <Card label="Walking Challenge" title={`${companion.cardEmoji} ${companion.name}`}>
        <p style={{ fontSize: '0.85rem', color: 'var(--stone)', marginBottom: '0.25rem' }}>
          {todaySteps.toLocaleString()} / {goal.toLocaleString()} steps today
        </p>
        <div className="ch-today-bar">
          <div className="ch-today-bar-fill" style={{ width: `${goalPct}%` }} />
        </div>
        {goalPct >= 100 && (
          <p style={{ fontSize: '0.78rem', color: 'var(--sage)', fontStyle: 'italic', marginBottom: '0.5rem' }}>
            {companion.goalMsg}
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
          <button className="save-btn ready" style={{ fontSize: '0.8rem' }} onClick={onLogProgress}>
            Log progress
          </button>
          <button className="ghost-btn" style={{ fontSize: '0.8rem' }} onClick={() => scrollToSection('active')}>
            Continue challenge →
          </button>
        </div>
      </Card>

      <Card label="Today's Mission" title="Walk Mission">
        <div style={{
          background: missionDone ? 'var(--accent)' : 'var(--surf)',
          borderRadius: '10px', padding: '0.85rem 1rem', transition: 'background 0.3s',
        }}>
          <p style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: missionDone ? 'rgba(255,255,255,0.65)' : 'var(--stone)', marginBottom: '0.4rem' }}>
            {todayMission.emoji} Today's mission
          </p>
          <p style={{ fontSize: '0.92rem', lineHeight: 1.6, color: missionDone ? 'white' : 'var(--ink)', margin: 0 }}>
            {todayMission.text}
          </p>
          {missionDone && (
            <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', marginTop: '0.4rem', marginBottom: 0 }}>
              ✓ Completed today — great work
            </p>
          )}
        </div>
        <button className="ghost-btn" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }} onClick={() => scrollToSection('active')}>
          {missionDone ? 'View all missions →' : 'Go to mission →'}
        </button>
      </Card>
    </>
  )
}

// ── Active challenge card (expandable) ────────────────────────────────────────

function ActiveChallengeCard({ emoji, title, progressText, requestOpen, children }) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (requestOpen) setOpen(true)
  }, [requestOpen])

  return (
    <div className="ch-active-card">
      <div
        className="ch-active-header"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => e.key === 'Enter' && setOpen(o => !o)}
      >
        <div className="ch-active-meta">
          <span className="ch-active-emoji">{emoji}</span>
          <div>
            <p className="ch-active-title">{title}</p>
            {progressText && <p className="ch-active-progress">{progressText}</p>}
          </div>
        </div>
        <span className="ch-active-toggle">{open ? '▲ Close' : 'Continue →'}</span>
      </div>
      {open && <div className="ch-active-body">{children}</div>}
    </div>
  )
}

// ── Active section ────────────────────────────────────────────────────────────

function ActiveSection({ walkData, missionData, colourData, bingoData, openRequests }) {
  const today = todayISO()

  // Walking progress
  const companion = COMPANIONS[walkData?.companion ?? 'carl'] ?? COMPANIONS.carl
  const compLog = walkData?.logs?.[companion.id] ?? []
  const todaySteps = (compLog.find(e => e.date === today) ?? { steps: 0 }).steps
  const goal = walkData?.goal || 5000
  const totalSteps = Object.values(walkData?.logs || {}).reduce(
    (sum, log) => sum + (Array.isArray(log) ? log.reduce((s, e) => s + (e.steps || 0), 0) : 0), 0
  )
  const hasWalkProgress = totalSteps > 0

  // Mission progress
  const missionStreak = calcMissionStreak(missionData?.completions)
  const totalMissions = missionData?.completions?.length ?? 0

  // Colour walk progress
  const colour = COLOUR_WALK_PALETTE[(colourData?.idx ?? 0) % COLOUR_WALK_PALETTE.length]
  const colourFound = colourData?.found?.length ?? 0
  const hasColourProgress = (colourData?.found?.length ?? 0) > 0 || (colourData?.idx ?? 0) > 0

  // Bingo progress
  const bingoItems = bingoData?.items ?? []
  const bingoDone = bingoData?.done ?? []
  const bingoCompleted = bingoDone.filter(i => bingoItems[i] !== 'FREE').length
  const hasBingoProgress = bingoDone.length > 1

  const showWalking   = hasWalkProgress   || openRequests.has('walking')
  const showColour    = hasColourProgress || openRequests.has('colour-walk')
  const showBingo     = hasBingoProgress  || openRequests.has('bingo')
  const showHobby     = openRequests.has('hobby-spinner')

  const hasAnything = showWalking || showColour || showBingo || showHobby

  return (
    <Card label="Challenges" title="Active challenges">
      {/* Walk Missions — always shown */}
      <ActiveChallengeCard
        emoji="🎖️"
        title="Walk Missions"
        progressText={totalMissions > 0 ? `${totalMissions} completed · ${missionStreak} day streak` : "Complete today’s mission"}
        requestOpen={openRequests.has('walk-mission')}
      >
        <WalkMission />
      </ActiveChallengeCard>

      {/* Walking Adventure */}
      {showWalking && (
        <ActiveChallengeCard
          emoji={companion.cardEmoji}
          title="Walking Adventure"
          progressText={`${companion.name} · ${todaySteps.toLocaleString()}/${goal.toLocaleString()} steps today`}
          requestOpen={openRequests.has('walking')}
        >
          <WalkingChallenge />
        </ActiveChallengeCard>
      )}

      {/* Colour Walk */}
      {showColour && (
        <ActiveChallengeCard
          emoji="🎨"
          title="Colour Walk"
          progressText={`${colour.name} · ${colourFound}/7 days spotted`}
          requestOpen={openRequests.has('colour-walk')}
        >
          <ColourWalk />
        </ActiveChallengeCard>
      )}

      {/* Joy Bingo */}
      {showBingo && (
        <ActiveChallengeCard
          emoji="🎯"
          title="Joy Bingo"
          progressText={`${bingoCompleted}/24 completed${isBingoFull(bingoDone) ? ' · Card complete! 🎉' : ''}`}
          requestOpen={openRequests.has('bingo')}
        >
          <JoyBingo />
        </ActiveChallengeCard>
      )}

      {/* Hobby Spinner (if opened from Explore) */}
      {showHobby && (
        <ActiveChallengeCard
          emoji="🎡"
          title="Hobby Spinner"
          progressText="Discover something new"
          requestOpen={openRequests.has('hobby-spinner')}
        >
          <HobbySpinner />
        </ActiveChallengeCard>
      )}

      {!hasAnything && (
        <p style={{ fontSize: '0.85rem', color: 'var(--stone)', fontStyle: 'italic', textAlign: 'center', padding: '1rem 0' }}>
          No challenges started yet.{' '}
          <button
            className="ghost-btn"
            style={{ fontSize: '0.82rem', display: 'inline', padding: '0' }}
            onClick={() => scrollToSection('explore')}
          >
            Explore some →
          </button>
        </p>
      )}
    </Card>
  )
}

// ── Explore section ───────────────────────────────────────────────────────────

function ExploreSection({ walkData, colourData, bingoData, onStart }) {
  const today = todayISO()

  const totalSteps = Object.values(walkData?.logs || {}).reduce(
    (sum, log) => sum + (Array.isArray(log) ? log.reduce((s, e) => s + (e.steps || 0), 0) : 0), 0
  )

  function getChallengeStatus(id) {
    if (id === 'walking')      return totalSteps > 0 ? 'active' : 'notStarted'
    if (id === 'colour-walk')  return (colourData?.found?.length > 0 || colourData?.idx > 0) ? 'active' : 'notStarted'
    if (id === 'bingo')        return (bingoData?.done?.length > 1) ? (isBingoFull(bingoData.done) ? 'completed' : 'active') : 'notStarted'
    if (id === 'walk-mission') return 'active'
    if (id === 'hobby-spinner')return 'notStarted'
    if (id === 'guided-walk')  return 'placeholder'
    return 'notStarted'
  }

  return (
    <Card label="Challenges" title="Explore challenges ✦">
      <p style={{ fontSize: '0.85rem', color: 'var(--stone)', marginBottom: '1.25rem', lineHeight: 1.6 }}>
        Missions, adventures, and things to discover.
      </p>

      {CATALOGUE.map(group => (
        <div key={group.category}>
          <p className="ch-category-heading">{group.category}</p>
          {group.items.map(item => {
            const status = getChallengeStatus(item.id)
            return (
              <div key={item.id} className="ch-catalogue-card">
                <div className="ch-cat-info">
                  <span className="ch-cat-emoji">{item.emoji}</span>
                  <div>
                    <p className="ch-cat-name">{item.name}</p>
                    <p className="ch-cat-desc">{item.desc}</p>
                  </div>
                </div>
                {status === 'placeholder' ? (
                  <span className="ch-badge ch-badge--soon">Coming soon</span>
                ) : status === 'completed' ? (
                  <span className="ch-badge ch-badge--done">Completed ✓</span>
                ) : status === 'active' ? (
                  <button
                    className="ghost-btn"
                    style={{ fontSize: '0.78rem', flexShrink: 0 }}
                    onClick={() => onStart(item.id)}
                  >
                    Continue →
                  </button>
                ) : (
                  <button
                    className="ghost-btn"
                    style={{ fontSize: '0.78rem', flexShrink: 0 }}
                    onClick={() => onStart(item.id)}
                  >
                    Start →
                  </button>
                )}
              </div>
            )
          })}
        </div>
      ))}
    </Card>
  )
}

// ── Vault section ─────────────────────────────────────────────────────────────

function VaultSection() {
  return (
    <AdventureVault />
  )
}

// ── Completed section ─────────────────────────────────────────────────────────

function CompletedSection({ walkData, missionData, colourData, bingoData }) {
  const completedItems = []

  // Walk missions completed
  const totalMissions = missionData?.completions?.length ?? 0
  if (totalMissions > 0) {
    completedItems.push({
      emoji: '🎖️',
      name: 'Walk Missions',
      detail: `${totalMissions} mission${totalMissions !== 1 ? 's' : ''} completed`,
    })
  }

  // Colour walk — each index advancement = one colour completed
  const colourIdx = colourData?.idx ?? 0
  if (colourIdx > 0) {
    completedItems.push({
      emoji: '🎨',
      name: 'Colour Walk',
      detail: `${colourIdx} colour${colourIdx !== 1 ? 's' : ''} completed`,
    })
  }

  // Walking milestones per companion
  if (walkData?.logs) {
    COMPANION_ORDER.forEach(id => {
      const companion = COMPANIONS[id]
      const log = walkData.logs[id] ?? []
      const steps = Array.isArray(log) ? log.reduce((s, e) => s + (e.steps || 0), 0) : 0
      const unlockedCount = STEP_MILESTONES.filter(m => steps >= m).length
      if (unlockedCount > 0) {
        completedItems.push({
          emoji: companion.cardEmoji,
          name: `${companion.name}'s Adventure`,
          detail: `${unlockedCount} milestone${unlockedCount !== 1 ? 's' : ''} · ${steps.toLocaleString()} steps`,
        })
      }
    })
  }

  // Bingo card complete
  if (bingoData?.done && isBingoFull(bingoData.done)) {
    completedItems.push({
      emoji: '🎯',
      name: 'Joy Bingo',
      detail: 'Card complete! 🎉',
    })
  }

  if (completedItems.length === 0) {
    return (
      <Card label="Completed" title="Keep going! 🌟">
        <p style={{ fontSize: '0.88rem', color: 'var(--stone)', fontStyle: 'italic', textAlign: 'center', padding: '1rem 0' }}>
          Your completed challenges will appear here. Keep going! 🌟
        </p>
      </Card>
    )
  }

  return (
    <Card label="Completed" title="What you've done">
      {completedItems.map((item, i) => (
        <div key={i} className="ch-completed-item">
          <span className="ch-completed-emoji">{item.emoji}</span>
          <div>
            <p className="ch-completed-name">{item.name}</p>
            <p className="ch-completed-detail">{item.detail}</p>
          </div>
          <span className="ch-badge ch-badge--done" style={{ marginLeft: 'auto' }}>Completed ✓</span>
        </div>
      ))}
    </Card>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ChallengesPage({ initialSection = 'today' }) {
  const [walkData,    setWalkData]    = useState(null)
  const [missionData, setMissionData] = useState(null)
  const [colourData,  setColourData]  = useState(null)
  const [bingoData,   setBingoData]   = useState(null)
  const [activeSection, setActiveSection] = useState(initialSection)
  const [openRequests, setOpenRequests]   = useState(new Set())

  useEffect(() => {
    api.getWalkState('walking').then(d => setWalkData(d || {}))
    api.getWalkState('walk-mission').then(d => setMissionData(d || {}))
    api.getColourWalk().then(d => setColourData(d || { idx: 0, found: [] }))
    api.getBingo().then(d => setBingoData(d || { items: [], done: [], freeText: '' }))
  }, [])

  // Scroll to initialSection on mount
  useEffect(() => {
    if (initialSection === 'today') return
    const timer = setTimeout(() => {
      const el = document.getElementById(initialSection)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
    return () => clearTimeout(timer)
  }, [initialSection])

  // Scroll-based active section tracking
  useEffect(() => {
    function update() {
      const OFFSET = 128
      let current = SECTIONS[0].id
      for (const { id } of SECTIONS) {
        const el = document.getElementById(id)
        if (!el) continue
        if (el.getBoundingClientRect().top <= OFFSET) current = id
      }
      setActiveSection(current)
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [])

  function requestOpen(id) {
    setOpenRequests(prev => new Set([...prev, id]))
    setTimeout(() => scrollToSection('active'), 50)
  }

  function handleLogProgress() {
    requestOpen('walking')
  }

  return (
    <div className="page active">
      <div className="page-inner">

        <div className="hero">
          <p className="hero-eyebrow">Challenges</p>
          <h1>Challenges <em>✦</em></h1>
          <hr className="hero-rule" />
          <SectionWelcome text="Small adventures, daily discoveries. Choose a challenge and see where it takes you." />
        </div>

        <ChallengesSectionNav activeSection={activeSection} />

        <section id="today" className="dashboard-section">
          <TodaySection
            walkData={walkData}
            missionData={missionData}
            onLogProgress={handleLogProgress}
          />
        </section>

        <section id="active" className="dashboard-section">
          <ActiveSection
            walkData={walkData}
            missionData={missionData}
            colourData={colourData}
            bingoData={bingoData}
            openRequests={openRequests}
          />
        </section>

        <section id="explore" className="dashboard-section">
          <ExploreSection
            walkData={walkData}
            colourData={colourData}
            bingoData={bingoData}
            onStart={requestOpen}
          />
        </section>

        <section id="vault" className="dashboard-section">
          <VaultSection />
        </section>

        <section id="completed" className="dashboard-section">
          <CompletedSection
            walkData={walkData}
            missionData={missionData}
            colourData={colourData}
            bingoData={bingoData}
          />
        </section>

      </div>
    </div>
  )
}
